'use client';

import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import { ArrowSquareOut } from 'phosphor-icons';
import { cloneElement, forwardRef, isValidElement } from 'react';
import { c } from '../../../utils/tailwind-merge';
import { LinkV2Props } from './Link.v2.types';

export const linkVariants = cva(
  `rounded transition-colors duration-[120ms]
  focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue`,
  {
    variants: {
      variant: {
        link: `
          inline-flex items-start
          text-blue font-normal underline-offset-[0.25em]
          hover:text-blue-hover hover:underline
          active:text-blue-hover
          focus:text-blue focus:no-underline
          focus-visible:text-blue focus-visible:no-underline
          visited:text-blue-hover`,
        'link-in-text-primary': `
          text-blue font-normal underline underline-offset-[0.25em]
          hover:text-blue-hover hover:no-underline
          active:text-blue-hover
          focus:text-blue focus:no-underline
          focus-visible:text-blue focus-visible:no-underline
          visited:text-blue-hover visited:underline`,
        'link-in-text-secondary': `
          text-grey-900 font-normal underline underline-offset-[0.25em]
          hover:text-blue-hover hover:no-underline
          active:text-blue-hover
          focus:text-grey-900 focus:no-underline
          focus-visible:text-grey-900 focus-visible:no-underline
          visited:text-blue-hover visited:underline`,
      },
    },
    defaultVariants: {
      variant: 'link',
    },
  }
);

const sizeClasses = {
  medium: 'text-base leading-6 tracking-[-0.096px]',
  small: 'text-sm leading-5 tracking-[-0.028px]',
  xsmall: 'text-xs leading-5 tracking-[-0.024px]',
};

const iconSizeMap = {
  medium: 24,
  small: 20,
  xsmall: 16,
};

const isInTextVariant = (variant: string): boolean => {
  return (
    variant === 'link-in-text-primary' || variant === 'link-in-text-secondary'
  );
};

export const LinkV2 = forwardRef<HTMLAnchorElement, LinkV2Props>(
  (
    {
      className = '',
      variant = 'link',
      size = 'medium',
      openInANewTab = false,
      asChild = false,
      children,
      ...props
    },
    ref
  ) => {
    const isInText = isInTextVariant(variant);
    const mergedClassName = c(
      linkVariants({ variant }),
      {
        [sizeClasses[size]]: !isInText,
      },
      className
    );

    const externalLinkProps = openInANewTab
      ? { target: '_blank', rel: 'noopener noreferrer' }
      : {};

    const externalLinkIcon = openInANewTab ? (
      <ArrowSquareOut
        size={isInText ? '1em' : iconSizeMap[size]}
        weight="fill"
        className="inline-block flex-shrink-0"
        aria-hidden="true"
      />
    ) : null;

    if (asChild) {
      if (!isValidElement(children)) {
        throw new Error(
          'LinkV2: When using asChild, children must be a single React element'
        );
      }

      const childElement = children as React.ReactElement;

      if (openInANewTab) {
        return (
          <Slot
            {...externalLinkProps}
            {...props}
            className={mergedClassName}
            ref={ref}
          >
            {cloneElement(childElement, {
              children: (
                <>
                  {childElement.props.children}
                  {externalLinkIcon}
                </>
              ),
            })}
          </Slot>
        );
      }

      return (
        <Slot
          {...externalLinkProps}
          {...props}
          className={mergedClassName}
          ref={ref}
        >
          {children}
        </Slot>
      );
    }

    return (
      <a
        ref={ref}
        className={mergedClassName}
        {...externalLinkProps}
        {...props}
      >
        {children}
        {externalLinkIcon}
      </a>
    );
  }
);

LinkV2.displayName = 'LinkV2';
